import React, { useEffect, useState } from 'react';
import { ItemToReturn } from '@src/models/returns';
import { RETURN_METHOD, ReturnMethodType } from '@src/constants/general';
import { setUpdatedItem } from '@src/stores/returnStore';
import { getFeatureSwitchStatic } from '@src/utils/general';
import CardMethod from './CardMethod';
import CardMethodDescription from './CardMethodDescription';
import ItemImageWithQuantityBadge from '../shared/items/ItemWithQuantityBadge';
import ItemReturnMethodError from './errors/ItemReturnMethodError';

interface ItemReturnMethodProps {
  item: ItemToReturn;
  showError?: boolean;
  // SORIS is all-or-nothing across the selection: true only when every selected
  // item qualifies. Derived once by the parent from the selection, so a mixed
  // selection falls all items back to plain In-Store (never per-item SORIS).
  sorisForSelection?: boolean;
}

const headerMap: { [key in ReturnMethodType]: string } = {
  'In-Store': 'In-Store Return',
  UPS: 'UPS Drop-Off',
  LORP: 'Return Pickup',
  SORIS: 'In-Store Return',
  APO_FPO: 'Military Post Office Drop-Off',
};

const ItemReturnMethod = ({
  item,
  showError = false,
  sorisForSelection = false,
}: ItemReturnMethodProps) => {
  // TODO: when SORIS is online, we need to default to In-Store at the start
  const [selectedCard, setSelectedCard] = useState<ReturnMethodType | ''>(
    item.returnMethod || '',
  );

  // If SORIS is currently blocked, we have to make sure basic In-Store return is always allowed for the item.
  const allowSORIS = getFeatureSwitchStatic('allowSORIS');

  const itemMethodOptions = (
    Object.values(RETURN_METHOD) as Array<ReturnMethodType>
  )
    .filter((option) => {
      // SORIS is all-or-nothing across the selection — offered only when every
      // selected item qualifies (never per-item), else all fall back to In-Store.
      if (option === 'SORIS') return sorisForSelection;
      // Hide the plain In-Store card when SORIS is being offered for the selection
      // (SORIS renders as "In-Store Return"); otherwise show it for in-store items
      // (or always, when SORIS is switched off entirely).
      if (option === 'In-Store') {
        if (sorisForSelection) return false;
        return !allowSORIS || item.eligibilities?.['In-Store'] === true;
      }
      // APO/FPO and UPS are mutually exclusive
      if (option === RETURN_METHOD.APO_FPO) return item.eligibilities?.APO_FPO;
      if (option === RETURN_METHOD.UPS_DROP_OFF && item.eligibilities?.APO_FPO)
        return false;
      return item.eligibilities?.[option] === true;
    })
    .sort((optA, optB) => {
      if (optA === RETURN_METHOD.APO_FPO) return -1;
      if (optB === RETURN_METHOD.APO_FPO) return 1;
      return 0;
    }) as ReturnMethodType[];

  const handleMethodClick = (method: ReturnMethodType) => {
    setSelectedCard(method);
  };

  useEffect(() => {
    if (selectedCard === '') return;
    const updatedItem = { ...item, returnMethod: selectedCard };
    setUpdatedItem(updatedItem);
  }, [selectedCard]);

  return (
    <div data-testid="item-return-method">
      <div key={item?.lineItemId} className="sui-relative sui-mb-4">
        <ItemImageWithQuantityBadge
          itemDescription={item?.itemDescription}
          imageUrl={item?.imageUrl}
          returnQuantity={item?.returnQuantity}
        />
      </div>

      {showError && !selectedCard && <ItemReturnMethodError />}

      <div className="sui-flex sui-flex-col sui-gap-4">
        {itemMethodOptions.map((option) => {
          return (
            <CardMethod
              key={option}
              header={headerMap[option]}
              onClick={() => handleMethodClick(option)}
              selected={selectedCard === option}
              returnCost={option === RETURN_METHOD.APO_FPO ? 'TBD' : 'FREE'}
            >
              <CardMethodDescription method={option} />
            </CardMethod>
          );
        })}
      </div>
    </div>
  );
};

export default ItemReturnMethod;
